import React from 'react';
import { useTheme } from 'styled-components';

import type { ProcessingJob } from 'api/processingJobsClient';
import { ErrorMessage } from 'components/common/ErrorMessage';
import Styled from './RunWorkflowModal.styled';

const {
  StyledJobCard,
  StyledJobHeader,
  StyledJobTitle,
  StyledJobStatus,
  StyledStatusPulse,
  StyledJobId,
  StyledStatsRow,
  StyledCompletedCount,
  StyledSkippedCount,
  StyledErrorTargetTag,
  StyledErrorTargetCode,
  StyledOutputBlock,
  StyledOutputLabel,
  StyledOutputPre,
  StyledClearHighlightsButton,
  StyledJobErrorSlot,
} = Styled;

const TERMINAL_STATUSES: string[] = ['succeeded', 'failed', 'skipped'];

interface OwnProps {
  job: ProcessingJob;
  isRunning: boolean;
  onClearColors: () => void;
}

const JobStatusCard = ({ job, isRunning, onClearColors }: OwnProps) => {
  const theme = useTheme();

  const statusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'succeeded':
        return theme.colors.statusSucceeded;
      case 'failed':
        return theme.colors.statusFailed;
      case 'skipped':
        return theme.colors.statusSkipped;
      default:
        return theme.colors.statusRunning;
    }
  };

  const completedCount = job.runtimeInfo?.completedSteps?.filter((s) => !s.isSkipped).length ?? 0;
  const skippedCount = job.runtimeInfo?.completedSteps?.filter((s) => s.isSkipped).length ?? 0;

  return (
    <StyledJobCard>
      <StyledJobHeader>
        <StyledJobTitle>Job Status</StyledJobTitle>
        <StyledJobStatus $color={statusColor(job.status)}>
          {isRunning && <StyledStatusPulse />}
          {job.status}
        </StyledJobStatus>
      </StyledJobHeader>
      <StyledJobId>{job.processingJobId}</StyledJobId>
      <StyledStatsRow>
        <span>
          <StyledCompletedCount>{completedCount}</StyledCompletedCount> completed
        </span>
        <span>
          <StyledSkippedCount>{skippedCount}</StyledSkippedCount> skipped
        </span>
      </StyledStatsRow>
      {job.error && (
        <StyledJobErrorSlot>
          <ErrorMessage>
            {job.error.errorMessage ?? job.error.message ?? JSON.stringify(job.error)}
            {job.error.target && (
              <StyledErrorTargetTag>
                (step: <StyledErrorTargetCode>{job.error.target}</StyledErrorTargetCode>)
              </StyledErrorTargetTag>
            )}
          </ErrorMessage>
        </StyledJobErrorSlot>
      )}
      {job.output && Object.keys(job.output).length > 0 && (
        <StyledOutputBlock>
          <StyledOutputLabel>Output</StyledOutputLabel>
          <StyledOutputPre>{JSON.stringify(job.output, null, 2)}</StyledOutputPre>
        </StyledOutputBlock>
      )}
      {TERMINAL_STATUSES.includes(job.status) && (
        <StyledClearHighlightsButton
          type='button'
          onClick={onClearColors}
          className='btn-ghost btn-sm'
          title='Remove step color highlights from the canvas'
        >
          Clear Highlights
        </StyledClearHighlightsButton>
      )}
    </StyledJobCard>
  );
};

const MemoJobStatusCard = React.memo(JobStatusCard);

export { MemoJobStatusCard as JobStatusCard };
export default MemoJobStatusCard;
